import { normalizePlaceTreeNames } from './placeTreeNames.js';
import { placeTreeBackupStore } from './placeTreeBackupStore.js';
import { createPlaceTreeAutomation } from './placeTreeAutomationSettings.js';

export const PLACE_TREE_SESSION_STORAGE_KEY = 'adenrel.place-tree-draft.v1';
export const PLACE_TREE_SESSION_DRAFT_VERSION = 1;
export const PLACE_TREE_SAVE_PROGRESS_KEY = 'adenrel.place-tree-save-progress.v1';

// Item saves change the ID map, not the reviewed world. Keep their durable
// checkpoint small instead of serializing the entire world after each Item.
export const savePlaceTreeSaveProgress = (progress, storage) => {
  try {
    const targetStorage = getSessionStorage(storage);
    if (!targetStorage) throw new Error('Session storage is unavailable.');
    const serialized = JSON.stringify(progress);
    targetStorage.setItem(PLACE_TREE_SAVE_PROGRESS_KEY, serialized);
    return { ok: true, saved: true, byteLength: serialized.length };
  } catch (error) { return backupFailure(error); }
};

const restoreSaveProgress = (draft, storage) => {
  try {
    const progress = JSON.parse(storage.getItem(PLACE_TREE_SAVE_PROGRESS_KEY));
    if (progress?.sessionId && progress.sessionId === draft.worldSaveProgress?.sessionId &&
      isRecord(progress.savedIds)) {
      return { ...draft, worldSaveProgress: progress, isDraftStructureLocked: true,
        savedDraftPlaceId: progress.savedIds[draft.draftPlace?.draftPlaceId || 'draft-place:root'] || draft.savedDraftPlaceId };
    }
  } catch { /* The complete draft remains usable without a valid checkpoint. */ }
  return draft;
};

const storageStates = new WeakMap();
const getBackupPointer = storage => {
  try { return JSON.parse(storage?.getItem(PLACE_TREE_SESSION_STORAGE_KEY))?.largeBackupId || ''; }
  catch { return ''; }
};
const getStorageState = storage => {
  if (!storageStates.has(storage)) storageStates.set(storage, { generation: 0, tail: Promise.resolve(), backupId: getBackupPointer(storage) });
  return storageStates.get(storage);
};
const enqueueBackup = (state, operation) => {
  state.tail = state.tail.catch(() => {}).then(operation);
  return state.tail;
};
const backupFailure = error => ({ ok: false, saved: false, byteLength: 0, error });

const isRecord = (value) =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const createEmptyGraph = () => ({
  rootPlaceId: '',
  placesById: {},
  beingsById: {},
  itemsById: {},
});

const normalizeGraph = (graph) => {
  if (!isRecord(graph)) {
    return createEmptyGraph();
  }

  return {
    rootPlaceId: String(graph.rootPlaceId ?? ''),
    placesById: isRecord(graph.placesById) ? graph.placesById : {},
    beingsById: isRecord(graph.beingsById) ? graph.beingsById : {},
    itemsById: isRecord(graph.itemsById) ? graph.itemsById : {},
  };
};

const normalizeArray = (value) => (Array.isArray(value) ? value : []);
const normalizeRecord = (value) => (isRecord(value) ? value : {});

export const createEmptyPlaceTreeSessionDraft = () => ({
  automation: createPlaceTreeAutomation(),
  prompt: '',
  selectedParentPlaceId: '',
  sourcePrompt: '',
  graph: createEmptyGraph(),
  logEntries: [],
  lastRunSummary: null,
  selectedNodeKey: '',
  draftPlace: null,
  draftChildSuggestions: [],
  draftInhabitantSuggestions: [],
  draftHouseholdManifests: {},
  draftCommercialInventoryManifest: null,
  draftCommercialInventoryItems: [],
  draftEstablishmentCurrencyHoldings: [],
  draftNotableLoadoutManifest: null,
  draftNotableLoadoutItems: [],
  draftNotableCurrencyHoldings: [],
  draftEvents: [],
  worldSaveProgress: null,
  worldDevelopment: { nearbyPlaces: [], nearbyInhabitants: [], finalRecords: {}, relationships: [], socialOutlines: {}, availableRelateTypes: [] },
  selectedPopulationTranche: 'notables',
  savedDraftPlaceId: '',
  isDraftStructureLocked: false,
});

export const hasPlaceTreeSessionContent = (draft) => {
  if (!isRecord(draft)) {
    return false;
  }

  const graph = normalizeGraph(draft.graph);
  return Boolean(
    String(draft.prompt ?? '').trim() ||
    String(draft.selectedParentPlaceId ?? '').trim() ||
    String(draft.sourcePrompt ?? '').trim() ||
    draft.draftPlace ||
    normalizeArray(draft.draftChildSuggestions).length ||
    normalizeArray(draft.draftInhabitantSuggestions).length ||
    Object.keys(normalizeRecord(draft.draftHouseholdManifests)).length ||
    draft.draftCommercialInventoryManifest ||
    normalizeArray(draft.draftCommercialInventoryItems).length ||
    normalizeArray(draft.draftEstablishmentCurrencyHoldings).length ||
    draft.draftNotableLoadoutManifest ||
    normalizeArray(draft.draftNotableLoadoutItems).length ||
    normalizeArray(draft.draftNotableCurrencyHoldings).length ||
    normalizeArray(draft.draftEvents).length ||
    normalizeArray(draft.worldDevelopment?.nearbyPlaces).length ||
    graph.rootPlaceId ||
    Object.keys(graph.placesById).length ||
    Object.keys(graph.beingsById).length ||
    Object.keys(graph.itemsById).length ||
    String(draft.savedDraftPlaceId ?? '').trim()
  );
};

const compactPlaceTreeSessionDraft = (draft) => {
  draft = normalizePlaceTreeNames(draft);
  const emptyDraft = createEmptyPlaceTreeSessionDraft();
  const childSuggestions = normalizeArray(draft?.draftChildSuggestions);
  const inhabitantSuggestions = normalizeArray(draft?.draftInhabitantSuggestions);
  const draftPlace = isRecord(draft?.draftPlace)
    ? { ...draft.draftPlace }
    : null;

  if (draftPlace) {
    // These arrays are authoritative at the top level and can be very large.
    delete draftPlace.childPlaceSuggestions;
    delete draftPlace.inhabitantSuggestions;
  }

  return {
    ...emptyDraft,
    automation: { ...createPlaceTreeAutomation(), ...normalizeRecord(draft?.automation) },
    prompt: String(draft?.prompt ?? ''),
    selectedParentPlaceId: String(draft?.selectedParentPlaceId ?? ''),
    sourcePrompt: String(draft?.sourcePrompt ?? ''),
    graph: normalizeGraph(draft?.graph),
    logEntries: normalizeArray(draft?.logEntries),
    lastRunSummary: isRecord(draft?.lastRunSummary)
      ? draft.lastRunSummary
      : null,
    selectedNodeKey: String(draft?.selectedNodeKey ?? ''),
    draftPlace,
    draftChildSuggestions: childSuggestions,
    draftInhabitantSuggestions: inhabitantSuggestions,
    draftHouseholdManifests: normalizeRecord(draft?.draftHouseholdManifests),
    draftCommercialInventoryManifest: isRecord(
      draft?.draftCommercialInventoryManifest
    )
      ? draft.draftCommercialInventoryManifest
      : null,
    draftCommercialInventoryItems: normalizeArray(
      draft?.draftCommercialInventoryItems
    ),
    draftEstablishmentCurrencyHoldings: normalizeArray(
      draft?.draftEstablishmentCurrencyHoldings
    ),
    draftNotableLoadoutManifest: isRecord(draft?.draftNotableLoadoutManifest)
      ? draft.draftNotableLoadoutManifest
      : null,
    draftNotableLoadoutItems: normalizeArray(draft?.draftNotableLoadoutItems),
    draftNotableCurrencyHoldings: normalizeArray(
      draft?.draftNotableCurrencyHoldings
    ),
    draftEvents: normalizeArray(draft?.draftEvents),
    worldSaveProgress: isRecord(draft?.worldSaveProgress) ? draft.worldSaveProgress : null,
    worldDevelopment: {
      mapContext: isRecord(draft?.worldDevelopment?.mapContext) ? draft.worldDevelopment.mapContext : null,
      nearbyPlaces: normalizeArray(draft?.worldDevelopment?.nearbyPlaces),
      nearbyInhabitants: normalizeArray(draft?.worldDevelopment?.nearbyInhabitants),
      relationships: normalizeArray(draft?.worldDevelopment?.relationships),
      socialOutlines: normalizeRecord(draft?.worldDevelopment?.socialOutlines),
      availableRelateTypes: normalizeArray(draft?.worldDevelopment?.availableRelateTypes),
      relationshipCoverage: isRecord(draft?.worldDevelopment?.relationshipCoverage) ? draft.worldDevelopment.relationshipCoverage : null,
      coverage: isRecord(draft?.worldDevelopment?.coverage) ? draft.worldDevelopment.coverage : null,
      coverageHistory: normalizeArray(draft?.worldDevelopment?.coverageHistory),
      coverageContextLoaded: Boolean(draft?.worldDevelopment?.coverageContextLoaded),
      coverageRequirements: Array.isArray(draft?.worldDevelopment?.coverageRequirements) ? draft.worldDevelopment.coverageRequirements : undefined,
      coverageSourcePrompt: String(draft?.worldDevelopment?.coverageSourcePrompt || ''),
      finalRecords: normalizeRecord(draft?.worldDevelopment?.finalRecords),
      spatialDiagnostics: normalizeRecord(draft?.worldDevelopment?.spatialDiagnostics),
      savedNearbyPlaces: normalizeArray(draft?.worldDevelopment?.savedNearbyPlaces),
      regionPlace: isRecord(draft?.worldDevelopment?.regionPlace) ? draft.worldDevelopment.regionPlace : null,
    },
    selectedPopulationTranche:
      String(draft?.selectedPopulationTranche ?? '').trim() || 'notables',
    savedDraftPlaceId: String(draft?.savedDraftPlaceId ?? ''),
    isDraftStructureLocked: Boolean(draft?.isDraftStructureLocked),
  };
};

const expandPlaceTreeSessionDraft = (draft) => {
  const compactDraft = compactPlaceTreeSessionDraft(draft);
  return {
    ...compactDraft,
    draftPlace: compactDraft.draftPlace
      ? {
          ...compactDraft.draftPlace,
          childPlaceSuggestions: compactDraft.draftChildSuggestions,
          inhabitantSuggestions: compactDraft.draftInhabitantSuggestions,
        }
      : null,
  };
};

const getSessionStorage = (storage) => {
  if (storage) {
    return storage;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  return window.sessionStorage;
};

export const savePlaceTreeSessionDraft = (draft, storage) => {
  try {
    const targetStorage = getSessionStorage(storage);
    if (!targetStorage) {
      throw new Error('Session storage is unavailable.');
    }

    if (!hasPlaceTreeSessionContent(draft)) {
      // Empty startup/recovery state is not an explicit request to discard a
      // backup. Only Clear Place Tree Draft may delete a stored draft.
      return { ok: true, saved: false, byteLength: 0 };
    }

    const serialized = JSON.stringify({
      version: PLACE_TREE_SESSION_DRAFT_VERSION,
      savedAt: new Date().toISOString(),
      data: compactPlaceTreeSessionDraft(draft),
    });
    const previousCheckpoint = targetStorage.getItem(PLACE_TREE_SAVE_PROGRESS_KEY);
    const clearIncludedCheckpoint = () => {
      if (targetStorage.getItem(PLACE_TREE_SAVE_PROGRESS_KEY) === previousCheckpoint) {
        targetStorage.removeItem(PLACE_TREE_SAVE_PROGRESS_KEY);
      }
    };
    const state = getStorageState(targetStorage);
    const generation = ++state.generation;
    try {
      if (!getBackupPointer(targetStorage)) {
        targetStorage.setItem(PLACE_TREE_SESSION_STORAGE_KEY, serialized);
        clearIncludedCheckpoint();
        return { ok: true, saved: true, byteLength: serialized.length };
      }
    } catch (error) {
      if (error.name !== 'QuotaExceededError' && !/quota/i.test(error.message)) throw error;
    }
    state.backupId ||= getBackupPointer(targetStorage) ||
      window.crypto?.randomUUID?.() || `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const id = state.backupId;
    const pending = enqueueBackup(state, async () => {
      if (generation !== state.generation) return { ok: true, saved: false, superseded: true };
      await placeTreeBackupStore.write(id, serialized);
      // A newer snapshot or explicit Clear takes precedence over an older write.
      if (generation === state.generation) {
        targetStorage.setItem(PLACE_TREE_SESSION_STORAGE_KEY,
          JSON.stringify({ version: PLACE_TREE_SESSION_DRAFT_VERSION, largeBackupId: id }));
        clearIncludedCheckpoint();
      }
      return { ok: true, saved: true, byteLength: serialized.length, largeBackup: true };
    }).catch(backupFailure);
    return {
      ok: true,
      saved: false,
      byteLength: serialized.length,
      pending,
    };
  } catch (error) {
    return { ok: false, saved: false, byteLength: 0, error };
  }
};

export const loadPlaceTreeSessionDraft = (storage) => {
  try {
    const targetStorage = getSessionStorage(storage);
    if (!targetStorage) {
      return null;
    }

    const serialized = targetStorage.getItem(PLACE_TREE_SESSION_STORAGE_KEY);
    if (!serialized) {
      return null;
    }

    const parsed = JSON.parse(serialized);
    if (parsed?.version === PLACE_TREE_SESSION_DRAFT_VERSION && parsed.largeBackupId) return null;
    if (
      parsed?.version !== PLACE_TREE_SESSION_DRAFT_VERSION ||
      !isRecord(parsed?.data)
    ) {
      targetStorage.removeItem(PLACE_TREE_SESSION_STORAGE_KEY);
      return null;
    }

    return restoreSaveProgress(expandPlaceTreeSessionDraft(parsed.data), targetStorage);
  } catch {
    try {
      getSessionStorage(storage)?.removeItem(PLACE_TREE_SESSION_STORAGE_KEY);
    } catch {
      // Nothing else can be done when storage itself is unavailable.
    }
    return null;
  }
};

export const hasLargePlaceTreeSessionDraft = storage => Boolean(getBackupPointer(getSessionStorage(storage)));

// IndexedDB survives closing a tab; its sessionStorage pointer does not.
// Enumerate surviving drafts so the user can recover them in a new tab.
export const listPlaceTreeBackups = async () => {
  const backups = [];
  for (const id of await placeTreeBackupStore.list()) {
    const serialized = await placeTreeBackupStore.read(id);
    try {
      const parsed = JSON.parse(serialized);
      if (parsed?.version !== PLACE_TREE_SESSION_DRAFT_VERSION || !hasPlaceTreeSessionContent(parsed?.data)) continue;
      const draft = parsed.data;
      backups.push({ id, savedAt: parsed.savedAt || '', name: draft.draftPlace?.name || 'Unnamed draft',
        places: (draft.draftPlace ? 1 : 0) + (draft.draftChildSuggestions?.length || 0) + (draft.worldDevelopment?.nearbyPlaces?.length || 0),
        beings: (draft.draftInhabitantSuggestions?.length || 0) + (draft.worldDevelopment?.nearbyInhabitants?.length || 0),
        items: (draft.draftCommercialInventoryItems?.length || 0) + (draft.draftNotableLoadoutItems?.length || 0),
        events: draft.draftEvents?.length || 0,
        savedRecords: Object.keys(draft.worldSaveProgress?.savedIds || {}).length,
      });
    } catch { /* Leave unreadable snapshots intact for manual recovery. */ }
  }
  return backups.sort((left, right) => right.savedAt.localeCompare(left.savedAt));
};

export const recoverPlaceTreeBackup = async (id, storage) => {
  const targetStorage = getSessionStorage(storage);
  if (!targetStorage) throw new Error('Session storage is unavailable.');
  const state = getStorageState(targetStorage);
  await state.tail.catch(() => {});
  const serialized = await placeTreeBackupStore.read(id);
  if (!serialized) throw new Error('The stored Place Tree draft could not be found.');
  const parsed = JSON.parse(serialized);
  if (parsed?.version !== PLACE_TREE_SESSION_DRAFT_VERSION || !hasPlaceTreeSessionContent(parsed?.data)) {
    throw new Error('The stored Place Tree draft is incompatible or empty.');
  }
  const restored = restoreSaveProgress(expandPlaceTreeSessionDraft(parsed.data), targetStorage);
  // Adopt only after validation succeeds. Do not delete the original backup.
  targetStorage.setItem(PLACE_TREE_SESSION_STORAGE_KEY,
    JSON.stringify({ version: PLACE_TREE_SESSION_DRAFT_VERSION, largeBackupId: id }));
  ++state.generation;
  state.backupId = id;
  return restored;
};

export const loadLargePlaceTreeSessionDraft = async storage => {
  const targetStorage = getSessionStorage(storage);
  if (!targetStorage) throw new Error('Session storage is unavailable.');
  await getStorageState(targetStorage).tail.catch(() => {});
  const id = getBackupPointer(targetStorage);
  if (!id) return loadPlaceTreeSessionDraft(targetStorage);
  const serialized = await placeTreeBackupStore.read(id);
  if (!serialized) throw new Error('The large Place Tree backup could not be found.');
  const parsed = JSON.parse(serialized);
  if (parsed.version !== PLACE_TREE_SESSION_DRAFT_VERSION || !isRecord(parsed.data)) throw new Error('The Place Tree backup is incompatible.');
  return restoreSaveProgress(expandPlaceTreeSessionDraft(parsed.data), targetStorage);
};

export const purgePlaceTreeBackups = async (storage) => {
  const targetStorage = getSessionStorage(storage);
  if (!targetStorage) throw new Error('Session storage is unavailable.');
  const state = getStorageState(targetStorage);
  ++state.generation;
  state.backupId = '';
  // Queue the clear after pending writes so an older write cannot restore a purged draft.
  await enqueueBackup(state, () => placeTreeBackupStore.clear());
  targetStorage.removeItem(PLACE_TREE_SESSION_STORAGE_KEY);
  targetStorage.removeItem(PLACE_TREE_SAVE_PROGRESS_KEY);
};

export const clearPlaceTreeSessionDraft = (storage) => {
  try {
    const targetStorage = getSessionStorage(storage);
    if (!targetStorage) {
      throw new Error('Session storage is unavailable.');
    }
    const state = getStorageState(targetStorage);
    ++state.generation;
    const id = getBackupPointer(targetStorage) || state.backupId;
    state.backupId = '';
    targetStorage.removeItem(PLACE_TREE_SESSION_STORAGE_KEY);
    targetStorage.removeItem(PLACE_TREE_SAVE_PROGRESS_KEY);
    if (id) return { ok: true, pending: enqueueBackup(state, async () => {
      await placeTreeBackupStore.remove(id);
      return { ok: true };
    }).catch(backupFailure) };
    return { ok: true };
  } catch (error) {
    return { ok: false, error };
  }
};
